import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif';

const CATEGORIES = {
  sale:    ['Product Sales', 'Shipping Revenue', 'Bulk Order', 'Wholesale', 'Other Income'],
  expense: ['Inventory Purchase', 'Shipping Cost', 'Rent', 'Utilities', 'Insurance', 'Marketing', 'Labor', 'Other'],
  refund:  ['Product Refund', 'Shipping Refund', 'Other'],
};

const PAYMENT_METHODS = ['Cash', 'Check', 'Credit Card', 'ACH/Wire', 'Shopify', 'Other'];
const STATUSES = ['paid', 'pending', 'overdue'];

const INP = { width: '100%', padding: '11px 14px', border: 'none', borderRadius: 10, fontSize: 15, outline: 'none', background: '#F2F2F7', color: '#1C1C1E', fontFamily: FONT, boxSizing: 'border-box' };
const LBL = { display: 'block', fontSize: 11, fontWeight: 600, color: '#8E8E93', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8, fontFamily: FONT };

export default function TransactionModal({ transaction, onSave, onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    type: 'sale', amount: '', description: '', date: today, category: 'Product Sales',
    paymentMethod: 'Cash', status: 'paid', customerName: '', invoiceNumber: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (transaction) setForm({ ...transaction, amount: String(transaction.amount) });
  }, [transaction]);

  function set(k, v) {
    setForm(f => {
      const next = { ...f, [k]: v };
      if (k === 'type') next.category = CATEGORIES[v][0];
      return next;
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.description.trim()) { setError('Description is required.'); return; }
    if (!form.amount || isNaN(parseFloat(form.amount))) { setError('Valid amount is required.'); return; }
    setSaving(true); setError('');
    try {
      await onSave({ ...form, amount: parseFloat(form.amount) });
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, fontFamily: FONT }}>
      <div style={{ background: 'white', borderRadius: 20, width: '100%', maxWidth: 560, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.28)' }}>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 16px' }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1C1C1E', margin: 0 }}>{transaction ? 'Edit Transaction' : 'Add Transaction'}</h2>
          <button onClick={onClose} style={{ background: '#F2F2F7', border: 'none', cursor: 'pointer', borderRadius: 50, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8E8E93' }}>
            <X size={15} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '0 24px 24px' }}>
          {error && <div style={{ background: '#FFF1F0', borderRadius: 10, padding: '10px 14px', color: '#FF3B30', fontSize: 13, marginBottom: 16 }}>{error}</div>}

          {/* Type toggle */}
          <div style={{ marginBottom: 16 }}>
            <label style={LBL}>Type</label>
            <div style={{ display: 'flex', gap: 0, background: '#E5E5EA', borderRadius: 11, padding: 3 }}>
              {['sale', 'expense', 'refund'].map(t => (
                <button key={t} type="button" onClick={() => set('type', t)} style={{
                  flex: 1, padding: '8px 0', borderRadius: 9, border: 'none', cursor: 'pointer', fontFamily: FONT,
                  background: form.type === t ? '#fff' : 'transparent',
                  boxShadow: form.type === t ? '0 1px 4px rgba(0,0,0,0.15)' : 'none',
                  color: form.type === t ? '#1C1C1E' : '#8E8E93',
                  fontWeight: form.type === t ? 600 : 400,
                  fontSize: 13, textTransform: 'capitalize',
                  transition: 'all 0.15s ease',
                }}>{t}</button>
              ))}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={LBL}>Amount ($) *</label>
              <input type="number" min="0" step="0.01" value={form.amount} onChange={e => set('amount', e.target.value)} required style={INP} placeholder="0.00" />
            </div>
            <div>
              <label style={LBL}>Date</label>
              <input type="date" value={form.date} onChange={e => set('date', e.target.value)} style={INP} />
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={LBL}>Description *</label>
            <input type="text" value={form.description} onChange={e => set('description', e.target.value)} required style={INP} placeholder="Enter description…" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={LBL}>Category</label>
              <select value={form.category} onChange={e => set('category', e.target.value)} style={INP}>
                {(CATEGORIES[form.type] || []).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={LBL}>Status</label>
              <select value={form.status} onChange={e => set('status', e.target.value)} style={INP}>
                {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={LBL}>Payment Method</label>
            <select value={form.paymentMethod} onChange={e => set('paymentMethod', e.target.value)} style={INP}>
              {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>

          {form.type === 'sale' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <div>
                <label style={LBL}>Customer Name</label>
                <input type="text" value={form.customerName} onChange={e => set('customerName', e.target.value)} style={INP} placeholder="Customer name" />
              </div>
              <div>
                <label style={LBL}>Invoice Number</label>
                <input type="text" value={form.invoiceNumber} onChange={e => set('invoiceNumber', e.target.value)} style={INP} placeholder="INV-001" />
              </div>
            </div>
          )}

          <div style={{ marginBottom: 24 }}>
            <label style={LBL}>Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} style={{ ...INP, resize: 'vertical' }} placeholder="Optional notes…" />
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={{ padding: '11px 22px', border: 'none', borderRadius: 12, background: '#F2F2F7', color: '#1C1C1E', cursor: 'pointer', fontSize: 14, fontWeight: 500, fontFamily: FONT }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ padding: '11px 26px', border: 'none', borderRadius: 12, background: '#84CC16', color: 'white', cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600, opacity: saving ? 0.8 : 1, fontFamily: FONT }}>
              {saving ? 'Saving…' : (transaction ? 'Update' : 'Add Transaction')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
